import React, { useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { alpha, styled, useTheme } from '@mui/material/styles';
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill';
import arrowIosDownwardFill from '@iconify/icons-eva/arrow-ios-downward-fill';
import {
  Box,
  List,
  Collapse,
  ListItemText,
  ListItemButton,
  ListItemIcon,
} from '@mui/material';
import color from 'styles/color';

export const ListItemStyle = styled((props) => (
  <ListItemButton disableGutters {...props} />
))(({ theme }) => ({
  ...theme.typography.body2,
  height: 48,
  position: 'relative',
  textTransform: 'capitalize',
  paddingLeft: theme.spacing(5),
  paddingRight: theme.spacing(2.5),
  color: theme.palette.text.secondary,
  '&:before': {
    top: 0,
    right: 0,
    width: 3,
    bottom: 0,
    content: "''",
    display: 'none',
    position: 'absolute',
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
    backgroundColor: theme.palette.primary.main,
  },
}));

export const ListItemIconStyle = styled(ListItemIcon)({
  width: 22,
  height: 22,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

interface IProps {
  item: TMenu;
  active: (path: string) => boolean;
}

function ChildSideNavItem({ item, active }: IProps) {
  const { title, path } = item;
  const isActiveSub = active(path);

  const activeSubStyle = {
    color: 'text.primary',
    fontWeight: 'fontWeightMedium',
  };

  return (
    <ListItemStyle
      key={title}
      component={RouterLink}
      to={path}
      sx={{
        ...(isActiveSub && activeSubStyle),
      }}
    >
      <ListItemIconStyle>
        <Box
          component='span'
          sx={{
            width: 4,
            height: 4,
            display: 'flex',
            borderRadius: '50%',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'text.disabled',
            transition: (theme) => theme.transitions.create('transform'),
            ...(isActiveSub && {
              transform: 'scale(2)',
              bgcolor: 'primary.main',
            }),
          }}
        />
      </ListItemIconStyle>
      <ListItemText disableTypography primary={title} />
    </ListItemStyle>
  );
}

export function SideNavItem({ item, active }: IProps) {
  const theme = useTheme();
  const isActiveRoot = active(item.path);
  const { title, path, icon, info, children } = item;
  const [open, setOpen] = useState<boolean>(isActiveRoot);

  const handleOpen = () => {
    setOpen((prev) => {
      return !prev;
    });
  };

  const activeRootStyle = {
    // color: 'primary.main',
    fontWeight: 'fontWeightMedium',
    bgcolor: color.gray00,
    // '&:before': { display: 'block' },
  };

  if (children) {
    return (
      <>
        <ListItemStyle
          onClick={handleOpen}
          sx={{
            ...(isActiveRoot && activeRootStyle),
          }}
        >
          <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
          <ListItemText disableTypography primary={title} />
          {info && info}
          <Box
            component={Icon}
            icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
            sx={{ width: 16, height: 16, ml: 1 }}
          />
        </ListItemStyle>

        <Collapse in={open} timeout='auto' unmountOnExit>
          <List component='div' disablePadding>
            {children.map((childMenu: TMenu) => (
              <ChildSideNavItem
                key={childMenu.title}
                item={childMenu}
                active={active}
              />
            ))}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <ListItemStyle
      component={RouterLink}
      to={path}
      sx={{
        ...(isActiveRoot && activeRootStyle),
      }}
    >
      <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
      <ListItemText disableTypography primary={title} />
      {info && info}
    </ListItemStyle>
  );
}
